/** Response row from GET /product/sub_category/default-models */
export interface DefaultSubcategoryModelRow {
	sub_category_id?: number;
	subCategoryId?: number;
	default_model3d_asset_url?: string | null;
	defaultModel3dAssetUrl?: string | null;
	default_model3d_asset_path?: string | null;
	defaultModel3dAssetPath?: string | null;
}

/** Extract rows from API body (supports raw array or { data }). */
export function coerceDefaultModelsRows(payload: unknown): DefaultSubcategoryModelRow[] {
	if (Array.isArray(payload)) return payload as DefaultSubcategoryModelRow[];
	if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
		return (payload as { data: DefaultSubcategoryModelRow[] }).data;
	}
	if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
		return (payload as { results: DefaultSubcategoryModelRow[] }).results;
	}
	return [];
}

/** Build `sub_category_id` → downloadable GLB/Gltf URL. */
export function buildSubCategoryDefaultModelUrlMap(
	rows: DefaultSubcategoryModelRow[]
): Record<number, string> {
	const out: Record<number, string> = {};
	for (const row of rows) {
		if (!row || typeof row !== 'object') continue;
		const idRaw = row.sub_category_id ?? row.subCategoryId;
		if (idRaw === undefined || idRaw === null) continue;
		const sid = typeof idRaw === 'number' ? idRaw : Number(idRaw);
		if (!Number.isFinite(sid)) continue;

		const url = row.default_model3d_asset_url ?? row.defaultModel3dAssetUrl;
		const path = row.default_model3d_asset_path ?? row.defaultModel3dAssetPath;
		let resolved = typeof url === 'string' && url.trim().length > 0 ? url.trim() : '';
		if (!resolved && typeof path === 'string' && path.trim().startsWith('http')) {
			resolved = path.trim();
		}
		if (resolved) out[sid] = resolved;
	}
	return out;
}

/** Every default GLB URL from the API map (for bulk preload / 3D cache). */
export function collectAllDefaultSubcategoryModelUrls(
	map: Record<number, string> | null | undefined
): string[] {
	if (!map || typeof map !== 'object') return [];
	const seen = new Set<string>();
	const out: string[] = [];
	for (const v of Object.values(map)) {
		if (typeof v === 'string' && v.trim().length > 0 && !seen.has(v)) {
			seen.add(v);
			out.push(v.trim());
		}
	}
	return out.sort();
}

export function getProductDirectModelUrl(product: unknown): string | null {
	if (!product || typeof product !== 'object') return null;
	const p = product as Record<string, unknown>;
	const u =
		(p.three_dmodel_url as string | undefined) ||
		(p.threeDmodelUrl as string | undefined) ||
		(p.threeDModelUrl as string | undefined) ||
		(p.model3dAsset as string | undefined) ||
		(p.model3dAssetPath as string | undefined);
	return typeof u === 'string' && u.length > 0 ? u : null;
}

export function getProductSubcategoryIdForDefaults(product: unknown): number | null {
	if (!product || typeof product !== 'object') return null;
	const p = product as Record<string, unknown>;

	// Check top-level keys first
	let raw = p.product_sub_category_id ?? p.productSubCategoryId ?? p.subCategoryId;

	// Check nested keys if not found
	if (raw === undefined || raw === null || raw === '') {
		const sc = (p.subCategory ?? p.productSubCategory ?? p.product_sub_category) as
			| Record<string, unknown>
			| undefined;
		if (sc && typeof sc === 'object') {
			raw = sc.id ?? sc.sub_category_id ?? sc.subCategoryId;
		}
	}

	if (raw === undefined || raw === null || raw === '') return null;
	const n = typeof raw === 'number' ? raw : Number(raw);
	return Number.isFinite(n) ? n : null;
}

/**
 * Quotation / explorer: product URL first, then sub-category default map, else null (bounding box).
 */
export function resolveQuotationSidebarModelAssetUrl(
	product: unknown,
	subCategoryDefaultModelUrlById: Record<number, string> | null | undefined
): string | null {
	const direct = getProductDirectModelUrl(product);
	if (direct) return direct;
	const sid = getProductSubcategoryIdForDefaults(product);
	if (sid === null || !subCategoryDefaultModelUrlById) return null;
	const fallback = subCategoryDefaultModelUrlById[sid];
	return typeof fallback === 'string' && fallback.length > 0 ? fallback : null;
}
